import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config/api';

/**
 * Handles the Facebook OAuth callback at /auth/facebook/callback.
 * The Cloudflare Worker redirects here with ?code=AUTH_CODE.
 * We POST the code to the backend for exchange.
 */
const FacebookCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Completing sign in...');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const code = searchParams.get('code');
    const authError = searchParams.get('error');

    if (authError) {
      setError(`Facebook sign in was cancelled or failed: ${authError.replace(/_/g, ' ')}`);
      setTimeout(() => navigate('/', { replace: true }), 3000);
      return;
    }

    if (!code) {
      setError('No authorization code received. Please try signing in again.');
      setTimeout(() => navigate('/', { replace: true }), 3000);
      return;
    }

    exchangeCode(code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCode = async (code) => {
    try {
      setStatus('Exchanging credentials...');

      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/auth/facebook/exchange`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.withCredentials = true;
        xhr.timeout = 20000;

        xhr.onload = function () {
          let data;
          try { data = JSON.parse(xhr.responseText); } catch (e) { data = { detail: xhr.responseText }; }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        };
        xhr.onerror = () => reject(new Error('Network request failed'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send(JSON.stringify({ code }));
      });

      if (!res.ok) {
        const detail = res.data?.detail || 'Code exchange failed';
        throw new Error(detail.replace(/_/g, ' '));
      }

      const userData = res.data;

      if (userData.session_token) {
        localStorage.setItem('session_token', userData.session_token);
      }

      localStorage.setItem('user_data', JSON.stringify({
        user_id: userData.user_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
      }));

      // Auto-apply pending referral code
      const pendingReferral = localStorage.getItem('pending_referral_code');
      if (pendingReferral && userData.session_token) {
        try {
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${BACKEND_URL}/api/billing/referral/apply`, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Authorization', `Bearer ${userData.session_token}`);
            xhr.withCredentials = true;
            xhr.timeout = 10000;
            xhr.onload = () => resolve();
            xhr.onerror = () => reject();
            xhr.ontimeout = () => reject();
            xhr.send(JSON.stringify({ code: pendingReferral }));
          });
        } catch { /* silent */ }
        localStorage.removeItem('pending_referral_code');
      }

      setStatus('Success! Redirecting...');
      await new Promise(r => setTimeout(r, 400));
      window.location.href = '/stash';

    } catch (err) {
      setError(`Sign in failed: ${err.message}. Please try again.`);
      setTimeout(() => navigate('/', { replace: true }), 3000);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p data-testid="facebook-callback-error" className="text-charcoal font-medium mb-2">{error}</p>
          <p className="text-gray-500 text-sm">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage mx-auto"></div>
        <p data-testid="facebook-callback-status" className="mt-4 text-charcoal">{status}</p>
      </div>
    </div>
  );
};

export default FacebookCallback;
